<template>
	<div>
		<h2 class="content-block">View Statements</h2>
		<b-form
			class="dx-card wide-card form"
			@submit.stop.prevent="findStatements"
		>
			<b-row>
				<b-col>
					<date-range label='Date of statement import' :date-range="dateRange" @dateUpdate="dateUpdate"/>
				</b-col>
				<b-col>
					<b-form-group label="Statement source" label-for="statement-source">
						<DxDropDownBox
							:value.sync="comparisonSourceText"
							:opened.sync="isDropDownBoxOpened"
							placeholder="Select statement source"
						>
							<DxList
								:data-source="statementSources"
								display-expr="name"
								selection-mode="single"
								@selection-changed="changeDropDownBoxValue"
							/>
						</DxDropDownBox>
					</b-form-group>
				</b-col>
			</b-row>
			<div class="d-flex justify-content-end mt-3">
				<!--small class="text-muted ml-auto mr-3 align-self-center">Max xx statements per search</small-->
				<div>
					<b-button
						slot="finish"
						type="submit"
						:disabled="loading || !comparisonSourceValue"
						variant="primary"
					>
						View imported statements<span
						v-if="loading"
						class="fa fa-spinner fa-pulse fa-fw ml-2"
					/><span v-else class="fa fa-search fa-fw ml-2"/>
					</b-button>
				</div>
			</div>
		</b-form>
		<dx-data-grid
			:ref="dataGridRefKey"
			:data-source="store"
			:show-borders="true"
			:remote-operations="remoteOperations"
			:allow-column-reordering="false"
			:allow-column-resizing="true"
			:row-alternation-enabled="true"
			:hover-state-enabled="true"
			:word-wrap-enabled="true"
			:column-auto-width="true"
			:show-row-lines="true"
			:show-column-lines="true"
			class="dx-card wide-card"
			width="100%"
		>
			<dx-column-chooser :enabled="false"/>
			<dx-export :enabled="true" :file-name="`${currentDate}-${$route.name}`"/>
			<dx-paging
				:page-size="pageSizes[0]"
			/>
			<dx-pager
				:allowed-page-sizes="pageSizes"
				:show-page-size-selector="true"
				:show-navigation-buttons="true"
				:show-info="true"
				:visible="true"
			/>
			<dx-filter-row :visible="true"/>
			<dx-header-filter :visible="false"/>
			<dx-filter-panel :visible="true"/>
			<dx-column
				data-field="id"
				:format="config.idFormat"
				:sort-index="0"
				sort-order="desc"
			/>
			<dx-column
				data-field="statementFormatId"
				:lookup="statementFormatColumnLookup"
				caption="Statement Format"
				:allow-sorting="false"
			/>
			<dx-column
				data-field="saved"
				data-type="datetime"
				:format="config.intlDateTimeFormat"
			/>
			<dx-column
				data-field="userIdUploaded"
				:lookup="userColumnLookup"
				caption="Uploaded by"
			/>
			<dx-column
				data-field="imported"
				data-type="datetime"
				:format="config.intlDateTimeFormat"
			/>
			<dx-column
				data-field="matched"
				data-type="datetime"
				:format="config.intlDateTimeFormat"
			/>
			<dx-column
				data-field="firstRecord"
				data-type="datetime"
				:format="config.intlDateTimeFormat"
				:allow-sorting="false"
			/>
			<dx-column
				data-field="lastRecord"
				data-type="datetime"
				:format="config.intlDateTimeFormat"
				:allow-sorting="false"
			/>
			<dx-column data-field="totalRecords"
					   :format="config.idFormat"
					   :allow-sorting="false"
			/>
			<dx-column
				data-field="deleted"
				data-type="datetime"
				:format="config.intlDateTimeFormat"
			/>
			<dx-column
				data-field="userIdDeleted"
				:lookup="userColumnLookup"
				caption="Deleted by"
				:allow-sorting="false"
			/>
			<dx-column
				caption="Actions"
				cell-template="button-cell-template"
				:allow-sorting="false"
			/>
			<template #button-cell-template="{data}">
				<div>
					<dx-button icon="rowfield" text="View" @click="showStatement(data)"/>
				</div>
			</template>
		</dx-data-grid>
	</div>
</template>

<script>
	import {mapState} from 'vuex'
	import {DxColumn, DxColumnChooser, DxDataGrid, DxExport, DxFilterPanel, DxFilterRow, DxHeaderFilter, DxPager, DxPaging,} from 'devextreme-vue/data-grid'
	import {DxDropDownBox, DxTagBox} from 'devextreme-vue'
	import DateRange from '@/components/date-range'
	import {config} from '@/config'
	import DxList from 'devextreme-vue/list'
	import {DxButton} from 'devextreme-vue/button'
	import CustomStore from 'devextreme/data/custom_store'

	const dataGridRefKey = 'dataGridRefKey'

	function isNotEmpty(value) {
		return value !== undefined && value !== null && value !== '';
	}

	export default {
		name: 'ViewStatements',
		components: {
			DateRange,
			DxColumn,
			DxColumnChooser,
			DxDataGrid,
			DxExport,
			DxFilterRow,
			DxFilterPanel,
			DxHeaderFilter,
			DxPager,
			DxPaging,
			// eslint-disable-next-line vue/no-unused-components
			DxTagBox,
			DxList,
			DxDropDownBox,
			DxButton,
		},
		data() {
			return {
				config,
				store: new CustomStore({
					load: async loadOptions => {
						if (this.comparisonSourceValue === null) {
							return {
								'totalCount': 0,
								'data': []
							};
						}

						const dataGrid = this.$refs['dataGridRefKey'].instance;
						let options = {};
						options['from'] = this.dateRange.startDate.getTime();
						options['to'] = this.dateRange.endDate.getTime();
						options['pageCount'] = dataGrid.pageCount();
						options['pageIndex'] = dataGrid.pageIndex();
						options['pageSize'] = dataGrid.pageSize();
						if (loadOptions['sort']) {
							this.lastSortedColumnDesc = options['sortDesc'] = loadOptions['sort'][0]['desc'];
							this.lastSortedColumn = options['sortColumn'] = loadOptions['sort'][0]['selector'];
						} else {
							options['sortDesc'] = this.lastSortedColumnDesc;
							options['sortColumn'] = this.lastSortedColumn;
						}
						if (loadOptions['filter']) {
						    options['filter'] = encodeURIComponent(JSON.stringify(loadOptions['filter']));
						}


						let params = "?"

						Object.keys(options)
							.forEach((i) => {
								if (isNotEmpty(options[i])) {
									params += `${i}=${options[i]}&`;
								}
							})

						params = params.slice(0, -1);

						return fetch(`reconciliation/admin/statement/imported/${this.comparisonSourceValue.id}${params}`)
							.then(response => response.json())
							.then(response => {
								this.loading = false;
								return {
									data: response.data,
									totalCount: response.totalCount
								};
							})
							.catch(() => {
								throw 'Network error'
							});
					}
				}),
				customStoreInit: false,
				pageSizes: config.itemsPerPage,
				dateRange: {
					startDate: new Date(new Date().getTime() - 7 * config.oneDay),
					endDate: new Date(),
				},
				comparisonSourceValue: null,
				comparisonSourceText: null,
				isDropDownBoxOpened: false,
				loading: false,
				dataGridRefKey,
				remoteOperations: {
					sorting: true,
					paging: true,
					filtering: true
				},
				lastSortedColumn: null,
				lastSortedColumnDesc: null
			}
		},
		computed: {
			...mapState('user', [
				'statementSources',
				'statementFormatColumnLookup',
				'userColumnLookup',
			]),
			currentDate() {
				return new Date().toISOString().split('T')[0]
			},
		},
		created() {
		},
		destroyed() {
		},
		methods: {
			findStatements() {
				this.loading = true;
				this.$refs['dataGridRefKey'].instance.pageIndex(0);
				this.$refs['dataGridRefKey'].instance.refresh();
			},
			showStatement(event) {
				this.$router.push({path: `/statement/${event.data.id}`})
			},
			dateUpdate(dateRange) {
				this.dateRange = Object.assign({}, this.dateRange, dateRange)
			},
			changeDropDownBoxValue(e) {
				this.comparisonSourceValue = e.addedItems[0]
				this.comparisonSourceText = this.comparisonSourceValue.name
				this.isDropDownBoxOpened = false
			}
		},
	}
</script>
<style lang="scss">
	td[role='columnheader'] {
		text-align: center !important;
	}

	.dx-freespace-row {
		height: 0 !important;
	}
</style>
